"use client";

import {
  Button,
  cn,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  History,
  LayoutGrid,
  MapPin,
  Megaphone,
  ScanBarcodeIcon,
  ShoppingBag,
  Store,
  Trash2,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { ShopPosBranchPicker } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosBranchPicker";

const POS_SALES_SEARCH = {
  offset: 0,
  limit: 20,
  status: undefined,
  createdFrom: undefined,
  createdTo: undefined,
  saleNumber: undefined,
  customerUserId: undefined,
  customerSearch: undefined,
} as const;

export type ShopPosPageToolbarProps = {
  simpleMode: boolean;
  toggleSimpleMode: () => void;
  branchId: string | null;
  branchTitle: string | null;
  shopIdShort: string;
  roleName: string | null | undefined;
  operatorLabel: string;
  shopId: string | undefined;
  posBranchOptions: { id: string; label: string }[];
  setSelectedPosBranchId: (id: string) => void;
  branchPickerLoading: boolean;
  needBranchesFromApi: boolean;
  branchesListError: boolean;
  hasShopWideBranchAccess: boolean;
  canListBranchesForPos: boolean;
  canSell: boolean;
  shiftsFeatureOn: boolean;
  blockedUntilShiftOpen: boolean;
  canReadShifts: boolean;
  canClockShift: boolean;
  cartLength: number;
  onClearCart: () => void;
  onFocusScan?: () => void;
  canReadPos: boolean;
  canReadPromotions: boolean;
  canReadMarketplace: boolean;
  canReadClaims: boolean;
  canReadSettlement: boolean;
};

type QuickActionDef = {
  key: string;
  label: string;
  icon: LucideIcon;
  className: string;
  disabled?: boolean;
  onClick?: () => void;
  to?: string;
  search?: Record<string, unknown>;
};

function QuickActionButton({
  action,
  shopId,
}: {
  action: QuickActionDef;
  shopId: string | undefined;
}) {
  const Icon = action.icon;
  const className = cn(
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
    "disabled:pointer-events-none disabled:opacity-50",
    action.className,
  );

  const content = (
    <>
      <Icon className="size-4 shrink-0" aria-hidden />
      <span>{action.label}</span>
    </>
  );

  if (action.to && shopId) {
    return (
      <Link
        to={action.to}
        params={{ shopId }}
        search={action.search}
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {content}
    </button>
  );
}

function AlertBanner({
  tone,
  children,
}: {
  tone: "error" | "warn" | "info";
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "rounded-lg px-3 py-2 text-xs leading-snug",
        tone === "error" &&
          "border border-destructive/40 bg-destructive/5 text-destructive",
        tone === "warn" &&
          "border border-border bg-muted/40 text-muted-foreground",
        tone === "info" &&
          "border border-primary/25 bg-primary/5 text-foreground",
      )}
    >
      {children}
    </p>
  );
}

export function ShopPosPageToolbar({
  simpleMode,
  toggleSimpleMode,
  branchId,
  branchTitle,
  shopIdShort,
  roleName,
  operatorLabel,
  shopId,
  posBranchOptions,
  setSelectedPosBranchId,
  branchPickerLoading,
  needBranchesFromApi,
  branchesListError,
  hasShopWideBranchAccess,
  canListBranchesForPos,
  canSell,
  shiftsFeatureOn,
  blockedUntilShiftOpen,
  canReadShifts,
  canClockShift,
  cartLength,
  onClearCart,
  onFocusScan,
  canReadPos,
  canReadPromotions,
  canReadMarketplace,
  canReadClaims,
  canReadSettlement,
}: ShopPosPageToolbarProps) {
  const quickActions: QuickActionDef[] = [];

  if (simpleMode && onFocusScan) {
    quickActions.push({
      key: "scan",
      label: "ສະແກນ",
      icon: ScanBarcodeIcon,
      onClick: onFocusScan,
      className:
        "border-emerald-500/35 bg-emerald-500/10 text-emerald-900 hover:bg-emerald-500/15 dark:text-emerald-100",
    });
  }

  if (shopId && canReadPos) {
    quickActions.push({
      key: "sales",
      label: "ປະຫວັດບິນ",
      icon: History,
      to: "/app/shops/$shopId/pos/sales",
      search: POS_SALES_SEARCH,
      className:
        "border-sky-500/35 bg-sky-500/10 text-sky-900 hover:bg-sky-500/15 dark:text-sky-100",
    });
  }

  quickActions.push({
    key: "clear",
    label: simpleMode ? "ຍົກເລີກ" : "ເລີ່ມໃໝ່",
    icon: Trash2,
    onClick: onClearCart,
    disabled: cartLength === 0,
    className:
      "border-border bg-background text-foreground hover:bg-muted disabled:opacity-40",
  });

  if (!simpleMode) {
    if (shopId && canReadPromotions) {
      quickActions.push({
        key: "promo",
        label: "ໂປຣໂມຊັນ",
        icon: Megaphone,
        to: "/app/shops/$shopId/promotions",
        search: { offset: 0, limit: 20, status: undefined },
        className:
          "border-violet-500/35 bg-violet-500/10 text-violet-950 hover:bg-violet-500/15 dark:text-violet-100",
      });
    }
    if (shopId && canReadMarketplace) {
      quickActions.push({
        key: "marketplace",
        label: "Marketplace",
        icon: ShoppingBag,
        to: "/app/shops/$shopId/marketplace",
        search: { offset: 0, limit: 20, q: undefined },
        className:
          "border-amber-500/35 bg-amber-500/10 text-amber-950 hover:bg-amber-500/15 dark:text-amber-100",
      });
    }
    if (shopId && canReadClaims) {
      quickActions.push({
        key: "claims",
        label: "ເຄສສິນຄ້າ",
        icon: Store,
        to: "/app/shops/$shopId/product-claims",
        search: {
          offset: 0,
          limit: 20,
          status: undefined,
          q: undefined,
        },
        className:
          "border-border bg-background text-foreground hover:bg-muted",
      });
    }
    if (shopId && canReadSettlement) {
      quickActions.push({
        key: "settlement",
        label: "Settlement",
        icon: Wallet,
        to: "/app/shops/$shopId/settlement",
        className:
          "border-border bg-background text-foreground hover:bg-muted",
      });
    }
  }

  const alerts: { tone: "error" | "warn" | "info"; message: string }[] = [];

  if (branchPickerLoading) {
    alerts.push({ tone: "info", message: "ກຳລັງໂຫຼດລາຍການສາຂາ…" });
  } else if (!branchId) {
    alerts.push({
      tone: "error",
      message:
        needBranchesFromApi && branchesListError
          ? "ໂຫຼດລາຍການສາຂາບໍ່ສຳເລັດ — ລອງໂຫຼດໜ້າຄືນ"
          : hasShopWideBranchAccess && !canListBranchesForPos
            ? "ຕ້ອງມີສິດອ່ານສາຂາ (shop_staff.read) ເພື່ອເລືອກສາຂາ POS"
            : "ບໍ່ມີສາຂາໃນບັນຊີພະນັກງານ — ຕິດຕໍ່ຜູ້ຄຸມຮ້ານ",
    });
  } else if (!canSell) {
    alerts.push({
      tone: "warn",
      message: simpleMode
        ? "ບໍ່ມີສິດຂາຍ — ຕິດຕໍ່ຜູ້ຄຸມຮ້ານ"
        : "ບໍ່ມີສິດຂາຍ (pos.sell) — ຊຳລະບໍ່ໃຊ້ງານ",
    });
  } else if (
    shiftsFeatureOn &&
    !blockedUntilShiftOpen &&
    (!canReadShifts || !canClockShift)
  ) {
    alerts.push({
      tone: "warn",
      message:
        "ຕ້ອງມີສິດກະ (shifts:read ແລະ shifts:clock) ເພື່ອເປີດກະກ່ອນຂາຍ",
    });
  }

  return (
    <header className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Title + context */}
      <div className="border-border border-b bg-muted/25 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold font-heading text-foreground text-xl uppercase tracking-tight sm:text-2xl">
                {simpleMode ? "ຂາຍ" : "ຊຳລະເງິນ"}
              </h1>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 font-semibold text-[10px] uppercase tracking-wide",
                  simpleMode
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {simpleMode ? "ໂໝດງ່າຍ" : "ໂໝດເຕັມ"}
              </span>
              {cartLength > 0 ? (
                <span className="rounded-full bg-secondary/15 px-2 py-0.5 font-medium text-secondary text-xs tabular-nums">
                  {cartLength} ລາຍການ
                </span>
              ) : null}
            </div>

            {simpleMode ? (
              <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
                ສະແກນ barcode ຫຼື ເລືອກສິນຄ້າເພື່ອເພີ່ມໃສ່ກະຕ່າ
              </p>
            ) : (
              <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
                ສະແກນ · ເລືອກຈາກໝວດ · ຫຼຸດລາຍການ/ບິນ · ຊຳລະ
              </p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {branchId != null &&
              branchTitle != null &&
              branchTitle !== "" &&
              posBranchOptions.length <= 1 ? (
                <span className="inline-flex items-center gap-1.5 text-sm">
                  <MapPin
                    className="size-3.5 shrink-0 text-primary"
                    aria-hidden
                  />
                  <span className="font-medium text-foreground">
                    {branchTitle}
                  </span>
                </span>
              ) : null}
              {!simpleMode ? (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {shopIdShort}
                  </span>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="text-muted-foreground">
                    {roleName ?? "—"} · {operatorLabel}
                  </span>
                </>
              ) : null}
            </div>

            <ShopPosBranchPicker
              options={posBranchOptions}
              value={branchId}
              onChange={setSelectedPosBranchId}
              shopId={shopId}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 shrink-0 gap-1.5 border-border px-3"
            onClick={toggleSimpleMode}
            title={
              simpleMode
                ? "ສະຫຼັບໄປໂໝດເຕັມ — ເມນູ ແລະ ຟີເຈີຄົບ"
                : "ສະຫຼັບໄປໂໝດງ່າຍ — ເຫມາະ cashier"
            }
          >
            <LayoutGrid className="size-4" aria-hidden />
            <span className="hidden sm:inline">
              {simpleMode ? "ໂໝດເຕັມ" : "ໂໝດງ່າຍ"}
            </span>
          </Button>
        </div>
      </div>

      {/* Quick actions */}
      {quickActions.length > 0 ? (
        <div className="border-border border-b px-4 py-3 sm:px-5">
          <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
            ວຽກດ່ວນ
          </p>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]">
            {quickActions.map((action) => (
              <QuickActionButton
                key={action.key}
                action={action}
                shopId={shopId}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Alerts */}
      {alerts.length > 0 ? (
        <div className="space-y-2 px-4 py-3 sm:px-5">
          {alerts.map((a) => (
            <AlertBanner key={a.message} tone={a.tone}>
              {a.message}
            </AlertBanner>
          ))}
        </div>
      ) : null}
    </header>
  );
}
